import {
  Accessibility,
  Activity,
  Apple,
  Bone,
  Brain,
  CigaretteOff,
  ClipboardList,
  Clock3,
  Droplets,
  Flame,
  Footprints,
  HeartPulse,
  MoonStar,
  PersonStanding,
  Pill,
  Salad,
  ScanHeart,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Smile,
  Sparkles,
  Stethoscope,
  Sun,
  ThermometerSun,
  Timer,
  Waves,
  Wind,
  type LucideIcon,
} from 'lucide-react'
import type { BlogPost } from '../data/blogs'

type VisualConfig = {
  main: LucideIcon
  support: LucideIcon
  detail: LucideIcon
  label: string
}

const visualCatalog: Record<string, VisualConfig> = {
  'understanding-blood-pressure': { main: HeartPulse, support: Activity, detail: ClipboardList, label: 'Know the pattern' },
  'building-a-balanced-plate': { main: Salad, support: Apple, detail: Droplets, label: 'Flexible balance' },
  'movement-that-fits-real-life': { main: PersonStanding, support: Footprints, detail: HeartPulse, label: 'Every move counts' },
  'better-sleep-starts-before-bed': { main: MoonStar, support: Sparkles, detail: Clock3, label: 'Build a sleep rhythm' },
  'vaccines-how-immune-memory-works': { main: ShieldCheck, support: Activity, detail: Sparkles, label: 'Immune memory' },
  'type-2-diabetes-beyond-sugar': { main: Droplets, support: Apple, detail: Activity, label: 'See the whole picture' },
  'colorectal-screening-informed-conversation': { main: ScanHeart, support: ShieldCheck, detail: ClipboardList, label: 'Screen with context' },
  'antibiotics-when-they-help': { main: Pill, support: ShieldCheck, detail: Clock3, label: 'Use medicines wisely' },
  'quitting-tobacco-plan-not-test': { main: CigaretteOff, support: HeartPulse, detail: Sparkles, label: 'Support beats willpower' },
  'medication-list-that-travels-with-you': { main: ClipboardList, support: Pill, detail: ShieldCheck, label: 'One current list' },
  'depression-deserves-care': { main: Brain, support: HeartPulse, detail: Sparkles, label: 'Care without judgment' },
  'heat-health-plan-ahead': { main: ThermometerSun, support: Droplets, detail: ShieldCheck, label: 'Plan before the heat' },
  'when-worry-becomes-more-than-stress': { main: Brain, support: Waves, detail: HeartPulse, label: 'Notice the pattern' },
  'asthma-symptoms-triggers-action-plan': { main: Wind, support: Activity, detail: ClipboardList, label: 'Breathe with a plan' },
  'kidney-disease-can-be-silent': { main: Activity, support: Droplets, detail: ShieldCheck, label: 'Tests reveal patterns' },
  'healthy-mouth-healthy-life': { main: Smile, support: ShieldCheck, detail: Sparkles, label: 'Small habits, daily' },
  'sun-protection-beyond-hot-days': { main: Sun, support: ShieldCheck, detail: ThermometerSun, label: 'Think UV, not heat' },
  'bone-strength-before-fracture': { main: Bone, support: PersonStanding, detail: ShieldCheck, label: 'Protect strength early' },
  'stroke-warning-signs-be-fast': { main: Brain, support: Timer, detail: Siren, label: 'BE FAST' },
  'heart-attack-warning-signs': { main: HeartPulse, support: Siren, detail: Clock3, label: 'Minutes matter' },
  'sepsis-signs-act-fast': { main: ShieldAlert, support: ThermometerSun, detail: Siren, label: 'Infection can escalate' },
  'heartburn-reflux-gerd': { main: Flame, support: Activity, detail: ShieldCheck, label: 'Follow the pattern' },
  'headache-patterns-red-flags': { main: Brain, support: Clock3, detail: ShieldAlert, label: 'Track, then assess' },
  'low-back-pain-recovery-red-flags': { main: Accessibility, support: PersonStanding, detail: ShieldAlert, label: 'Move with context' },
}

const fallback: VisualConfig = { main: Stethoscope, support: ShieldCheck, detail: Sparkles, label: 'Clear health guidance' }

export function ArticleVisual({ post, variant = 'card' }: { post: BlogPost; variant?: 'card' | 'hero' }) {
  const visual = visualCatalog[post.slug] ?? fallback
  const Main = visual.main
  const Support = visual.support
  const Detail = visual.detail

  return (
    <div className={`topic-visual topic-visual-${variant}`} role="img" aria-label={`${post.title}: ${visual.label}`}>
      <span className="topic-grid" aria-hidden="true" />
      <span className="topic-orbit topic-orbit-one" aria-hidden="true" />
      <span className="topic-orbit topic-orbit-two" aria-hidden="true" />
      <span className="topic-main"><Main aria-hidden="true" /></span>
      <span className="topic-support topic-support-one"><Support aria-hidden="true" /></span>
      <span className="topic-support topic-support-two"><Detail aria-hidden="true" /></span>
      <span className="topic-caption">{visual.label}</span>
    </div>
  )
}
